/**
 * Shared client types, query keys and small helpers for the admin dashboard
 * surface. The wire shapes mirror the backend admin contracts
 * (`AdminAnalyticsDto`, `AdminUserDto`/`AdminUserListResponse`,
 * `AdminQuizSummaryDto`/`AdminQuizListResponse`,
 * `AdminQuestionBankItemDto`/`AdminQuestionBankListResponse`) which serialize
 * as camelCase JSON. All admin endpoints are cookie-authed and require the
 * `Admin` policy server-side.
 */

export type AdminAnalytics = {
  liveSessions: number
  totalUsers: number
  totalOrganizations: number
  totalQuizzes: number
  totalBankItems: number
  publicBankItems: number
  privateBankItems: number
}

export type AdminUser = {
  id: string
  email: string | null
  displayName: string
  emailConfirmed: boolean
  organizationId: string
  organizationName: string
  roles: string[]
  isLockedOut: boolean
  createdAt: string
  lastLoginAt: string | null
}

export type AdminUserListResponse = {
  users: AdminUser[]
  total: number
  page: number
  pageSize: number
}

export type AdminQuiz = {
  id: string
  title: string
  description: string
  questionCount: number
  createdByUserId: string | null
  ownerDisplayName: string | null
  ownerEmail: string | null
  updatedAt: string
}

export type AdminQuizListResponse = {
  quizzes: AdminQuiz[]
  total: number
  page: number
  pageSize: number
}

export type AdminOrganization = {
  id: string
  name: string
  slug: string
  ownerEmail: string | null
  ownerDisplayName: string
  userCount: number
  quizCount: number
  createdAt: string
}

export type AdminOrganizationListResponse = {
  organizations: AdminOrganization[]
  total: number
  page: number
  pageSize: number
}

export type AdminSession = {
  id: string
  pin: string
  state: 'Lobby' | 'InQuestion' | 'ShowingResults' | 'ShowingLeaderboard' | 'Final'
  hostUserId: string | null
  hostDisplayName: string | null
  hostEmail: string | null
  quizId: string | null
  isRandom: boolean
  playerCount: number
  createdAt: string
  startedAt: string | null
  endedAt: string | null
}

export type AdminSessionListResponse = {
  sessions: AdminSession[]
  total: number
  page: number
  pageSize: number
}

export type AdminBankType = 'MultipleChoice' | 'TrueFalse' | 'OrderSequence'
export type AdminBankDifficulty = 'Easy' | 'Medium' | 'Hard'
export type AdminBankScope = 'Public' | 'Private'

export type AdminBankItem = {
  id: string
  type: AdminBankType
  text: string
  timeSeconds: number
  difficulty: AdminBankDifficulty | null
  categoryId: string | null
  categoryName: string | null
  scope: AdminBankScope
  organizationId: string | null
  createdByUserId: string | null
  options: string[] | null
  correctOptionIndex: number | null
  correctAnswer: boolean | null
  items: string[] | null
  explanation: string | null
  createdAt: string
  updatedAt: string
}

export type AdminBankListResponse = {
  items: AdminBankItem[]
  total: number
  page: number
  pageSize: number
}

export type AdminBankCategory = {
  id: string
  name: string
  scope: AdminBankScope
  itemCount: number
}

/** Result of a bulk import (`POST /admin/question-bank/import`). */
export type ImportRowError = {
  rowNumber: number
  code: string
  detail: string | null
}

export type ImportResult = {
  totalRows: number
  imported: number
  failed: number
  errors: ImportRowError[]
}

export const ADMIN_PAGE_SIZE = 20

/**
 * Stable query keys so every admin surface and every mutation invalidation
 * keys off identical tuples. The root arrays let mutations invalidate by
 * prefix (`['admin','users']` matches every paged/filtered user query).
 */
export const adminKeys = {
  analytics: ['admin', 'analytics'] as const,
  usersRoot: ['admin', 'users'] as const,
  users: (filters: { search: string; page: number }) =>
    ['admin', 'users', filters] as const,
  quizzesRoot: ['admin', 'quizzes'] as const,
  quizzes: (filters: { search: string; page: number }) =>
    ['admin', 'quizzes', filters] as const,
  organizationsRoot: ['admin', 'organizations'] as const,
  organizations: (filters: { search: string; page: number }) =>
    ['admin', 'organizations', filters] as const,
  sessionsRoot: ['admin', 'sessions'] as const,
  sessions: (filters: { status: 'all' | 'active'; page: number }) =>
    ['admin', 'sessions', filters] as const,
  bankRoot: ['admin', 'questionBank', 'items'] as const,
  bank: (filters: {
    search: string
    categoryId: string | null
    scope: string
    type: string
    page: number
  }) => ['admin', 'questionBank', 'items', filters] as const,
}

/**
 * Absolute date as `yyyy/mm/dd` with zero-padded month/day and Western/Latin
 * digits (e.g. "2026/05/29"). Built manually from the Date parts — no
 * `Intl.DateTimeFormat`/`toLocaleDateString` — so no locale-injected
 * directional marks (U+200F RLM) can scramble the date inside dir=ltr cells.
 */
export function formatDateAr(iso: string): string {
  const d = new Date(iso)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}/${m}/${day}`
}

/**
 * Absolute date and time as `yyyy/mm/dd HH:MM` (24-hour, zero-padded) with
 * Western/Latin digits (e.g. "2026/05/29 14:07"). Built manually from the Date
 * parts — no `Intl.DateTimeFormat`/`toLocaleString` — so no locale-injected
 * directional marks (U+200F RLM) can scramble the value inside dir=ltr cells.
 * Use for short-lived rows where the date alone is ambiguous (e.g. sessions).
 */
export function formatDateTimeAr(iso: string): string {
  const d = new Date(iso)
  const hh = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${formatDateAr(iso)} ${hh}:${min}`
}

/** Human-readable byte size for the selected-file affordance (e.g. "1.2 MB"). */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(1)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}
